import { Box, Typography } from '@mui/material';
import type { DynamicInvoicePageCustomer, DynamicInvoicePageInvoice } from '@vy/api-definition';
import { dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { type Currency, type Period } from '@vy/types';
import { Price } from '../../../../../../components/design/Price';
import { formatDateToMDY } from '../../../../../../utils/date';
import { InvoiceBillingStatusLabel } from '../../../../../../pages/invoices/InvoicesPage/invoices-page-components/InvoiceBillingStatusLabel';
import {
  getPartiallyPaidTooltipContent,
  isInvoicePartiallyPaid,
} from '../../../../../../pages/invoices/InvoicesPage/invoices-page-components/invoice-partially-paid-utils';

type InvoiceHeaderInfoProps = {
  customer: DynamicInvoicePageCustomer;
  invoice: DynamicInvoicePageInvoice;
  currency: Currency;
  isTrial?: boolean;
};

export const InvoiceHeaderInfo: React.FC<InvoiceHeaderInfoProps> = ({
  customer,
  invoice,
  currency,
  isTrial = false,
}) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box>
        <Typography
          variant="h5"
          color="textPrimary"
          sx={{
            fontSize: '24px',
            fontWeight: 300,
          }}
        >
          {`${customer.name} — ${invoice.name}`}
        </Typography>
        <Typography
          {...getSelectorAttribute(dynamicInvoicePageSelectors.header.billingCycle)}
          variant="subtitle1"
          color="textSecondary"
          sx={{
            fontSize: '16px',
            fontWeight: 400,
          }}
        >
          {formatBillingCycle(invoice.billingCycle)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {(isTrial || invoice.billingStatus) && (
              <Box sx={{ marginRight: 2 }}>
                <InvoiceBillingStatusLabel
                  billingStatus={invoice.billingStatus}
                  isTrial={isTrial}
                  showIndicator={isInvoicePartiallyPaid(invoice)}
                  tooltipContent={getPartiallyPaidTooltipContent(invoice)}
                  sx={{
                    padding: '6px',
                    fontSize: '13px',
                    height: 'auto',
                  }}
                />
              </Box>
            )}
            <Price
              {...getSelectorAttribute(dynamicInvoicePageSelectors.header.totalAmount)}
              fractionDigits={2}
              variant="h6"
              currency={currency}
            >
              {invoice.revenueBreakdown.total}
            </Price>
          </Box>
          <Typography
            variant="subtitle1"
            color="textSecondary"
            sx={{
              fontSize: '16px',
              fontWeight: 400,
            }}
          >
            Total Amount
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

function formatBillingCycle(period: Period) {
  const start = formatDateToMDY(period.startTime);
  const end = formatDateToMDY(period.endTime);

  return `${start} - ${end}`;
}
